import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Search, Earth, MapPin } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Input } from "../components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { countryName, getCurrencyInfo, searchCountries } from "../data/countries";
import AddTripModal from "../components/trasferte/AddTripModal";
import WorldMap from "../components/trasferte/WorldMap";
import CountryInfo from "../components/trasferte/CountryInfo";
import TripsPanel from "../components/trasferte/TripsPanel";
import ClientDetail from "../components/trasferte/ClientDetail";
import EditClientDialog from "../components/trasferte/EditClientDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Trasferte: state, the two requests, and which panel is showing. Everything
 * drawn on the screen lives in components/trasferte.
 *
 * This file held all of it and was 43 KB. Since nothing here can be edited in
 * place - a one-line change means reading and rewriting the whole file - that
 * size was the reason the section went untouched for so long.
 */
export default function Trasferte() {
  const { t, language } = useLanguage();

  const [clients, setClients] = useState([]);
  const [trips, setTrips] = useState([]);
  const [markedCodes, setMarkedCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingTrips, setLoadingTrips] = useState(false);

  const [selectedCode, setSelectedCode] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("info");

  // The client whose trips are open. Null means the whole list is showing.
  const [openClientId, setOpenClientId] = useState(null);

  const [addOpen, setAddOpen] = useState(false);
  const [addInitialClient, setAddInitialClient] = useState(null);
  // The trip being corrected, or null when the dialog is recording a new one.
  // The same dialog serves both, so this is what tells them apart.
  const [editingTrip, setEditingTrip] = useState(null);

  const [editOpen, setEditOpen] = useState(false);
  const [deleteTrip, setDeleteTrip] = useState(null);
  const [deleteClientOpen, setDeleteClientOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // Exchange rate cache: { [code]: {rate, fetchedAt, error} }
  const [rates, setRates] = useState({});

  // Every client, once. This one list feeds the type-ahead when a trip is
  // added. Client names are encrypted, so it could not be asked of the server
  // anyway.
  const fetchClients = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trasferte/clients`, { withCredentials: true });
      setClients(data);
    } catch (err) {
      toast.error(t("trasferte.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Which countries the map marks. Asked of the server rather than derived
  // from the client list: a trip recorded without a client belongs to a
  // country too, and built from the clients alone that country stayed grey -
  // which, since the panel is opened by clicking the map, left the trip
  // reachable only by searching for the country by name.
  //
  // Allowed to fail quietly. The clients still mark their own countries below,
  // so a failure here loses the trips without clients rather than the map.
  const fetchCountries = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/trasferte/countries`, { withCredentials: true });
      setMarkedCodes((data || []).map((row) => row.country_code).filter(Boolean));
    } catch (err) {
      setMarkedCodes([]);
    }
  }, []);

  useEffect(() => { fetchClients(); }, [fetchClients]);
  useEffect(() => { fetchCountries(); }, [fetchCountries]);

  // The trips of one country, fetched when it is selected and grouped in the
  // browser. Grouping here rather than asking the server per client keeps it to
  // one request and lets trips with no client sit in the same list.
  const fetchTrips = useCallback(async (code) => {
    if (!code) return;
    setLoadingTrips(true);
    try {
      const { data } = await axios.get(`${API}/trasferte`, {
        params: { country_code: code }, withCredentials: true,
      });
      setTrips(data);
    } catch (err) {
      toast.error(t("trasferte.loadError"));
    } finally {
      setLoadingTrips(false);
    }
  }, [t]);

  useEffect(() => {
    if (selectedCode) fetchTrips(selectedCode);
    else setTrips([]);
  }, [selectedCode, fetchTrips]);

  // A country is marked if anybody has been there or has a client there.
  const activeCodes = useMemo(() => {
    const s = new Set(markedCodes);
    clients.forEach((c) => { if (c.country_code) s.add(c.country_code); });
    return s;
  }, [clients, markedCodes]);

  const clientsHere = useMemo(
    () => (selectedCode ? clients.filter((c) => c.country_code === selectedCode) : []),
    [clients, selectedCode]
  );

  const { byClient, looseTrips } = useMemo(() => {
    const grouped = new Map();
    const loose = [];
    trips.forEach((trip) => {
      if (trip.client_id) {
        if (!grouped.has(trip.client_id)) grouped.set(trip.client_id, []);
        grouped.get(trip.client_id).push(trip);
      } else {
        loose.push(trip);
      }
    });
    return { byClient: grouped, looseTrips: loose };
  }, [trips]);

  const openClient = openClientId
    ? clientsHere.find((c) => c.id === openClientId) || null
    : null;
  const openClientTrips = openClientId ? byClient.get(openClientId) || [] : [];

  const selectedCountryName = selectedCode ? countryName(selectedCode, language) : null;
  const selectedCurrency = selectedCode ? getCurrencyInfo(selectedCode, language) : null;

  const searchResults = useMemo(
    () => searchCountries(searchQuery, language),
    [searchQuery, language]
  );

  // Fetch exchange rate when a country is selected (cached for the session).
  // Uses backend proxy (/api/trasferte/exchange-rate) to bypass CSP.
  useEffect(() => {
    if (!selectedCode || !selectedCurrency) return;
    const cur = selectedCurrency.currency_code;
    if (rates[cur]) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/trasferte/exchange-rate`,
          { params: { base: "EUR", target: cur }, withCredentials: true }
        );
        if (!data?.rate) throw new Error("rate missing in response");
        if (!cancelled) {
          setRates((p) => ({ ...p, [cur]: { rate: data.rate, fetchedAt: data.fetched_at || new Date().toISOString() } }));
        }
      } catch (err) {
        if (!cancelled) setRates((p) => ({ ...p, [cur]: { error: true } }));
      }
    })();
    return () => { cancelled = true; };
  }, [selectedCode, selectedCurrency, rates]);

  const handleSelectCountry = (code) => {
    setSelectedCode(code);
    setActiveTab("info");
    setOpenClientId(null);
    setSearchQuery("");
    setTimeout(() => {
      const el = document.getElementById("trasferte-detail-panel");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  // The marked countries are refreshed along with the rest: the first trip to
  // a country is what puts it on the map, and the last one deleted is what
  // takes it off again.
  const refresh = async () => {
    await Promise.all([fetchClients(), fetchCountries(), fetchTrips(selectedCode)]);
  };

  const openNewTrip = (forClient) => {
    setAddInitialClient(forClient || null);
    setEditingTrip(null);
    setAddOpen(true);
  };

  // Correcting one. The dialog needs the client as an object rather than an
  // id, since that is what it shows on its collapsed line; a trip with no
  // client passes null, which the dialog reads as decided rather than as
  // unanswered.
  const openEditTrip = (trip) => {
    const attached = trip.client_id
      ? clientsHere.find((c) => c.id === trip.client_id) || null
      : null;
    setAddInitialClient(attached);
    setEditingTrip(trip);
    setAddOpen(true);
  };

  const closeTripDialog = () => {
    setAddOpen(false);
    setEditingTrip(null);
  };

  const handleTripSaved = async (trip, client, wasEdit) => {
    closeTripDialog();
    toast.success(wasEdit ? t("trasferte.updated") : t("trasferte.created"));
    setActiveTab("trips");
    // A new trip moves the view to wherever it was filed, since that is where
    // it can be seen. A correction leaves the view alone: whoever made it is
    // already looking at the card and being moved somewhere else would read as
    // something having gone wrong.
    if (!wasEdit) setOpenClientId(client ? client.id : null);
    await refresh();
  };

  const handleConfirmDeleteTrip = async () => {
    if (!deleteTrip) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/trasferte/${deleteTrip.id}`, { withCredentials: true });
      toast.success(t("trasferte.deleted"));
      setDeleteTrip(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("trasferte.deleteError"));
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmDeleteClient = async () => {
    if (!openClient) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/trasferte/clients/${openClient.id}`, { withCredentials: true });
      toast.success(t("trasferte.clientDeleted"));
      setDeleteClientOpen(false);
      setOpenClientId(null);
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("trasferte.clientDeleteFailed"));
    } finally {
      setBusy(false);
    }
  };

  const currentRate = selectedCurrency ? rates[selectedCurrency.currency_code] : null;

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div>
          <Link
            to="/"
            data-testid="trasferte-back-to-home"
            className="inline-flex items-center text-sm text-slate-500 hover:text-indigo-700 mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            {t("projects.backToHome")}
          </Link>

          {/* Header */}
          <div className="flex items-center gap-3 mb-8">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm shrink-0"
              style={{ backgroundColor: "#9B7FD4" }}
            >
              <Earth className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">
              {t("trasferte.title")}
            </h1>
          </div>

          {/* Instruction + country search */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <p className="text-sm text-slate-600" data-testid="trasferte-select-instruction">
              {t("trasferte.selectInstruction")}
            </p>
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <Input
                data-testid="trasferte-search-input"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={t("trasferte.searchPlaceholder")}
                className="pl-9 border-slate-200"
              />
              {searchResults.length > 0 && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-30 max-h-72 overflow-y-auto">
                  {searchResults.map((r) => (
                    <button
                      key={r.code}
                      type="button"
                      data-testid={`trasferte-search-result-${r.code}`}
                      onClick={() => handleSelectCountry(r.code)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-indigo-50 flex items-center gap-3"
                    >
                      <img
                        src={`https://flagcdn.com/${r.code.toLowerCase()}.svg`}
                        alt=""
                        className="w-6 h-4 object-cover rounded-sm border border-slate-100 shrink-0"
                      />
                      <span className="text-slate-700 min-w-0 break-words">{r.name}</span>
                      <span className="ml-auto text-xs text-slate-400 shrink-0">{r.code}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <WorldMap
            activeCodes={activeCodes}
            selectedCode={selectedCode}
            language={language}
            t={t}
            onSelect={handleSelectCountry}
          />

          {/* Detail panel */}
          <div id="trasferte-detail-panel" className="mt-8">
            {!selectedCode ? (
              <div className="bg-white border border-slate-200 rounded-xl p-12 text-center" data-testid="trasferte-empty-state">
                <MapPin className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500">{t("trasferte.selectPrompt")}</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <div className="border-b border-slate-100 flex">
                  <TabButton active={activeTab === "info"} onClick={() => setActiveTab("info")} testId="trasferte-tab-info">
                    {t("trasferte.tabInfo")}
                  </TabButton>
                  <TabButton active={activeTab === "trips"} onClick={() => setActiveTab("trips")} testId="trasferte-tab-trips">
                    {t("trasferte.tabTrips")} ({trips.length})
                  </TabButton>
                </div>

                {activeTab === "info" ? (
                  <CountryInfo
                    code={selectedCode}
                    name={selectedCountryName}
                    currency={selectedCurrency}
                    rate={currentRate}
                    language={language}
                    t={t}
                  />
                ) : openClient ? (
                  <ClientDetail
                    client={openClient}
                    trips={openClientTrips}
                    language={language}
                    t={t}
                    onBack={() => setOpenClientId(null)}
                    onAddTrip={() => openNewTrip(openClient)}
                    onEdit={() => setEditOpen(true)}
                    onDeleteClient={() => setDeleteClientOpen(true)}
                    onEditTrip={openEditTrip}
                    onDeleteTrip={setDeleteTrip}
                  />
                ) : (
                  <TripsPanel
                    countryName={selectedCountryName}
                    clients={clientsHere}
                    byClient={byClient}
                    looseTrips={looseTrips}
                    loading={loadingTrips || loading}
                    language={language}
                    t={t}
                    onOpenClient={setOpenClientId}
                    onAddTrip={() => openNewTrip(null)}
                    onEditTrip={openEditTrip}
                    onDeleteTrip={setDeleteTrip}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Mounted only while open, and keyed on the trip: the dialog builds its
          fields from what it is given when it mounts, so without a key React
          would reuse one already on screen and a second edit would open on the
          first trip's answers. */}
      {addOpen && selectedCode && (
        <AddTripModal
          key={editingTrip ? editingTrip.id : "new"}
          open={addOpen}
          onClose={closeTripDialog}
          countryCode={selectedCode}
          countryName={selectedCountryName}
          clients={clientsHere}
          initialClient={addInitialClient}
          trip={editingTrip}
          onClientCreated={(c) => setClients((prev) => [{ ...c, visits: 0, last_visit: null }, ...prev])}
          onSaved={handleTripSaved}
        />
      )}

      {openClient && (
        <EditClientDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          client={openClient}
          t={t}
          onSaved={async () => {
            setEditOpen(false);
            toast.success(t("trasferte.clientSaved"));
            await refresh();
          }}
        />
      )}

      <AlertDialog open={!!deleteTrip} onOpenChange={(v) => { if (!v && !busy) setDeleteTrip(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("trasferte.deleteTripTitle")}</AlertDialogTitle>
            <AlertDialogDescription>{t("trasferte.deleteTripConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="trip-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmDeleteTrip(); }}
              disabled={busy}
              data-testid="trip-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy ? t("trasferte.deleting") : t("trasferte.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleteClientOpen} onOpenChange={(v) => { if (!v && !busy) setDeleteClientOpen(false); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("trasferte.deleteClient")}</AlertDialogTitle>
            <AlertDialogDescription>{t("trasferte.deleteClientConfirm")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleConfirmDeleteClient(); }}
              disabled={busy}
              data-testid="client-delete-confirm"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {busy ? t("trasferte.deleting") : t("trasferte.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function TabButton({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`px-5 py-3 text-sm font-medium transition-colors ${active ? "text-indigo-700 border-b-2 border-indigo-700 -mb-px" : "text-slate-500 hover:text-slate-700"}`}
    >
      {children}
    </button>
  );
}
